import { Fragment, useEffect, useState } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { ClockIcon } from '@heroicons/react/24/outline';
import { Button } from '@follow/ui';
import { ExtendOptions } from '../../services/RentalService';

interface ModalExtendRentalProps {
  visible: boolean;
  isLoadingOptions: boolean;
  options?: ExtendOptions;
  isSubmitting?: boolean;
  onCancel: () => void;
  onConfirm: (hours: number) => void;
}

const ModalExtendRental: React.FC<ModalExtendRentalProps> = ({
  visible,
  isLoadingOptions,
  options,
  isSubmitting,
  onCancel,
  onConfirm,
}) => {
  const [hours, setHours] = useState(1);
  const maxHours = options?.maxHours ?? 0;

  useEffect(() => {
    if (visible && options) {
      setHours(Math.min(1, options.maxHours) || 1);
    }
  }, [visible, options]);

  const newPrice = options ? options.currentPrice + (options.hourlyRate * hours) : undefined;
  const newDeposit = options ? options.currentDeposit - (options.hourlyRate * hours) : undefined;

  return (
    <Transition.Root show={visible} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onCancel}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-neutral-30 transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-lg bg-white p-6 shadow-card outline outline-1 outline-offset-[-1px] outline-neutral-10 transition-all">
                <div className="flex items-start gap-4">
                  <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-neutral-10">
                    <ClockIcon className="h-5 w-5 text-neutral-60" />
                  </div>
                  <div className="flex flex-col gap-1 min-w-0 flex-1">
                    <Dialog.Title as="h3" className="text-neutral typo-label">
                      Prolonger la réservation
                    </Dialog.Title>
                    <p className="text-neutral-80 typo-small">
                      Le prix est augmenté du tarif horaire de chaque équipement, prélevé sur la caution existante. Aucun nouveau paiement Stripe n&apos;est effectué.
                    </p>
                  </div>
                </div>

                <div className="mt-5">
                  {isLoadingOptions && (
                    <p className="typo-small text-neutral-50">Calcul des heures disponibles...</p>
                  )}
                  {!isLoadingOptions && options && maxHours < 1 && (
                    <p className="typo-small text-error-600">
                      Aucune heure supplémentaire disponible (équipement déjà réservé ensuite, ou caution insuffisante).
                    </p>
                  )}
                  {!isLoadingOptions && options && maxHours >= 1 && (
                    <div className="flex flex-col gap-3">
                      <div className="flex items-center justify-between gap-4">
                        <span className="typo-small text-neutral-50">Nombre d&apos;heures à ajouter</span>
                        <div className="flex items-center gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setHours((h) => Math.max(1, h - 1))}
                            disabled={hours <= 1}
                          >
                            −
                          </Button>
                          <input
                            type="number"
                            min={1}
                            max={maxHours}
                            value={hours}
                            onChange={(e) => {
                              const next = Number(e.target.value);
                              if (Number.isInteger(next)) setHours(Math.min(maxHours, Math.max(1, next)));
                            }}
                            className="w-14 text-center py-1 rounded-md outline outline-1 outline-offset-[-1px] outline-neutral-20 bg-neutral-5 typo-small"
                          />
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setHours((h) => Math.min(maxHours, h + 1))}
                            disabled={hours >= maxHours}
                          >
                            +
                          </Button>
                        </div>
                      </div>
                      <span className="typo-caption text-neutral-40">
                        Maximum {maxHours}h ({options.maxHoursByAvailability < options.maxHoursByDeposit ? 'limité par la disponibilité de l\'équipement' : 'limité par le montant de la caution'})
                      </span>
                      <div className="flex flex-col gap-1 pt-3 border-t border-neutral-10">
                        <div className="flex justify-between typo-small">
                          <span className="text-neutral-50">Nouveau prix</span>
                          <span className="text-neutral">{((newPrice ?? 0) / 100).toFixed(2)} €</span>
                        </div>
                        <div className="flex justify-between typo-small">
                          <span className="text-neutral-50">Nouvelle caution</span>
                          <span className="text-neutral">{((newDeposit ?? 0) / 100).toFixed(2)} €</span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                <div className="mt-6 flex justify-end gap-2">
                  <Button variant="outline" size="sm" onClick={onCancel}>
                    Annuler
                  </Button>
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={() => onConfirm(hours)}
                    disabled={isLoadingOptions || !options || maxHours < 1 || isSubmitting}
                    loading={isSubmitting}
                  >
                    {isSubmitting ? 'Prolongation...' : `Prolonger de ${hours}h`}
                  </Button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
};

export default ModalExtendRental;
